import { Permission, setupCardDone, type SetupChecklistStatus, type SetupTrack } from '@ruta/shared';
import { usePermission } from './usePermission';
import { useOrganization } from './useQueries';

export interface SetupChecklistState {
  /** The derived checklist, present only when the server chose to send it. */
  checklist: SetupChecklistStatus | undefined;
  /** Owners and admins hold COMPANY_MANAGE — the client-side belt to the server's suspenders. */
  canManageSetup: boolean;
  /** Show the guided intro: company manager + checklist present + not everything is done. */
  active: boolean;
  /** The three "Finish setting up" tracks, resolved by the server. Null when not sent. */
  tracks: SetupTrack[] | null;
  /** The Finish setting up card has something to show: company manager + tracks + not `setupCardDone`. */
  tracksActive: boolean;
}

/**
 * Guided-setup state, for the owner and anyone with COMPANY_MANAGE, for the Ruta AI intro (RUT-2955) and the
 * "Finish setting up" card on Live.
 *
 * The server is the authority: `GET /organization/profile` returns `setupChecklist`
 * ONLY for the owner or a COMPANY_MANAGE holder and ONLY while the checklist isn't dismissed. So a present
 * `checklist` already implies allowed + not-dismissed; the `canManageSetup` gate is a
 * belt-and-suspenders guard that also keeps the profile fetch from firing for
 * everyone else (and while unauthenticated, where the permission mask is empty).
 *
 * `active` follows the eight checklist items (Ruta AI's intro lists them all);
 * `tracksActive` follows the three tracks the card shows. They finish at
 * different times on purpose.
 */
export function useSetupChecklist(): SetupChecklistState {
  const canManageSetup = usePermission(Permission.COMPANY_MANAGE);
  const { data } = useOrganization({ enabled: canManageSetup });
  const checklist = data?.setupChecklist;
  const active = !!checklist && !checklist.allCompleted && canManageSetup;
  const tracks = checklist?.tracks ?? null;
  // setupCardDone, not tracksCompleted: a company that finished the old checklist
  // stays finished, and a carrier review that is only pending holds nothing up.
  const tracksActive = canManageSetup && !!tracks && !setupCardDone(checklist);
  return { checklist, canManageSetup, active, tracks, tracksActive };
}
